import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, Link as RouterLink, Route, Switch } from "react-router-dom";
import {
  useRowSelect,
  useTable,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
} from "react-table";
import { CustomCheckbox } from "../../../../util";
import UserForm from "../UserForm";

import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";

const useStyles = makeStyles((theme) => ({
  innerBox: {
    background: "#ffffff",
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  modal: {
    background: "#ffffff",
    width: "874px",
    top: `50%`,
    left: `50%`,
    transform: `translate(-50%, -50%)`,
    position: "absolute",
    padding: theme.spacing(3),
    margin: theme.spacing(3),
  },
  tableCell: {
    paddingTop: 3,
    paddingBottom: 3,
  },
  iconBtn: {
    color: "#CCCCCC",
    margin: 5,
  },
  buttonGroup: {
    margin: theme.spacing(3),
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
}));

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <CustomCheckbox type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <>
      <TextField
        id="search-field"
        label="Search News"
        variant="outlined"
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        margin="dense"
      />
      <Button
        type="submit"
        variant="outlined"
        color="primary"
        onClick={() => {
          onChange(value);
        }}
      >
        Search
      </Button>
    </>
  );
}

export default function ManageRolePage(props) {
  const classes = useStyles();
  const { match, history } = props;
  const role = match.params.role;
  const { setLoading } = useLoading();
  const {
    control,
  } = useForm();
  const [age, setAge] = React.useState("");
  const [open, setOpen] = React.useState(true);

  const getAdminsByRole = async () => {
    try {
      setLoading(true);
      const response = await apiHelper.getAdminsById(role);;
      setData(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    getAdminsByRole();
    history.push(match.url);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Name",
        accessor: "username",
      },
      {
        Header: "Email",
        accessor: "email",
      },
    ],
    []
  );

  const CustomLink = React.useMemo(
    () =>
      React.forwardRef((linkProps, ref) => <Link ref={ref} {...linkProps} />),
    []
  );

  const [data, setData] = useState([]);

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { globalFilter },
  } = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Let's make a column for selection
        {
          id: "selection",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            </div>
          ),
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div>
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            </div>
          ),
        },
        ...columns,
        {
          id: "action",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: () => <div>Action</div>,
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div>
              <IconButton
                className={classes.iconBtn}
                size="small"
                component={CustomLink}
                to={`${match.url}/${row.original.id}`}
                onClick={handleOpen}
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                className={classes.iconBtn}
                size="small"
                component={CustomLink}
              >
                <Delete fontSize="inherit" />
              </IconButton>
            </div>
          ),
        },
      ]);
    }
  );

  const handleChange = (event) => {
    setAge(event.target.value);
  };

  useEffect(() => {
    getAdminsByRole();
  }, []);

  return (
    <>
      <Typography variant="h4" gutterBottom>
        User Permission > Manage Role
      </Typography>
      <Box className={classes.innerBox}>
        <Grid container xs={12}>
          <Grid container item xs={6} direction="column" spacing={3}>
            <Grid item>
              <Typography variant="span">Manage Role</Typography>
            </Grid>
            <Grid item>
              <Controller
                name="enRoleName"
                control={control}
                defaultValue={role.charAt(0).toUpperCase() + role.slice(1)}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Role Name"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Box className={classes.innerBox}>
        <Grid container xs={12}>
          <Grid container item xs={12} direction="column" spacing={3}>
            <Grid item>
              <Typography variant="span">Manage Users</Typography>
            </Grid>
            <Grid container item justify="space-between">
              <Grid container item xl={4} md={6} xs={12}>
                <Grid item xs={6}>
                  <Box width="100%" component="span" display="flex">
                    <Box width="100%">
                      <FormControl fullWidth margin="dense" variant="outlined">
                        <InputLabel id="demo-simple-select-outlined-label">
                          Bulk Actions
                        </InputLabel>
                        <Select
                          labelId="demo-simple-select-outlined-label"
                          id="demo-simple-select-outlined"
                          value={age}
                          onChange={handleChange}
                          label="Bulk Actions"
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          <MenuItem value={"delete"}>Delete</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box display="flex">
                      <Button variant="outlined" color="primary">
                        Apply
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Grid container item xl={4} md={6} xs={12}>
                <Box
                  width="100%"
                  component="span"
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <Box display="flex">
                    <Button
                      variant="contained"
                      color="primary"
                      component={RouterLink}
                      to={`${match.url}/create`}
                      onClick={handleOpen}
                    >
                      Add New User
                    </Button>
                  </Box>
                  <GlobalFilter
                    preGlobalFilteredRows={preGlobalFilteredRows}
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                  />
                </Box>
              </Grid>
            </Grid>
            <Grid item spacing={3}>
              <Paper variant="outlined">
                <Table {...getTableProps()}>
                  <TableHead>
                    {headerGroups.map((headerGroup) => (
                      <TableRow {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <TableCell
                            {...column.getHeaderProps()}
                            className={classes.tableCell}
                          >
                            {column.render("Header")}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {rows.map((row, i) => {
                      prepareRow(row);
                      console.log(row);
                      return (
                        <TableRow {...row.getRowProps()}>
                          {row.cells.map((cell) => {
                            return (
                              <TableCell
                                {...cell.getCellProps()}
                                className={classes.tableCell}
                              >
                                {cell.render("Cell")}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      {/* <div className={classes.buttonGroup}>
        <Button
          type="submit"
          variant="contained"
          disableElevation
          style={{
            color: "white",
            background: "#306F9D",
            marginRight: "5px",
            padding: "9px 16px",
          }}
        >
          Save Change
        </Button>
      </div> */}

      <Switch>
        <Route
          exact
          path={`${match.url}/create`}
          render={(routeProps) => {
            return (
              <Modal
                open={open}
                onClose={() => history.push(match.url)}
                aria-labelledby="simple-modal-title"
                aria-describedby="simple-modal-description"
              >
                <Box className={classes.modal}>
                  <Grid container>
                    <Typography
                      variant="span"
                      gutterBottom
                      style={{ fontWeight: "bold" }}
                    >
                      Create User
                    </Typography>
                  </Grid>
                  <UserForm
                    {...routeProps}
                    mode="create"
                    handleClose={handleClose}
                    currentRole={match.params.role}
                  />
                </Box>
              </Modal>
            );
          }}
        />
        <Route
          path={`${match.url}/:id`}
          render={(routeProps) => {
            return (
              <Modal
                open={open}
                onClose={() => history.push(match.url)}
                aria-labelledby="simple-modal-title"
                aria-describedby="simple-modal-description"
              >
                <Box className={classes.modal}>
                  <Typography variant="span" gutterBottom>
                    Edit User
                  </Typography>
                  <UserForm
                    {...routeProps}
                    mode="edit"
                    handleClose={handleClose}
                    currentRole={match.params.role}
                  />
                </Box>
              </Modal>
            );
          }}
        />
      </Switch>
    </>
  );
}
